import type { ReactNode } from 'react';
import { CheckCheck, Eye, GitMerge, Info, RefreshCw, TriangleAlert, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import type { TaskActionKind } from './task-pr-actions';
import type { TaskAction } from './use-task-prs';

const ICONS: Record<TaskActionKind, ReactNode> = {
  merge: <GitMerge />,
  fix_conflicts: <Wrench />,
  update: <RefreshCw />,
  review: <Eye />,
  done: <CheckCheck />,
};

/** SF-T-237: Merge's icon while a run is still going on the task: a warning (or info, while it waits for people). */
export function RunNoteIcon({ tone }: { tone: 'warning' | 'info' }) {
  return tone === 'warning' ? <TriangleAlert className="text-warning-strong" aria-hidden /> : <Info className="text-info-strong" aria-hidden />;
}

/** The actions as the toolbar's buttons: off ones say why on hover; SF-T-237: Merge says so while a run is going. */
export function TaskPrActionButtons({ actions }: { actions: TaskAction[] }) {
  if (!actions.length) return null;
  return (
    <>
      {actions.map((a) => (
        <Button
          key={a.key}
          variant="outline"
          size="xs"
          layoutClassName="mr-1"
          disabled={!a.enabled}
          disabledReason={a.reason}
          loading={a.loading}
          data-hint={a.runNote ? `${a.hint}. ${a.runNote.text}.` : a.hint}
          onClick={a.onSelect}
        >
          {a.runNote ? <RunNoteIcon tone={a.runNote.tone} /> : ICONS[a.kind]}
          {a.label}
        </Button>
      ))}
    </>
  );
}

/** The actions as More menu items (phones): an off one shows why under its label. */
export function TaskPrActionMenuItems({ actions }: { actions: TaskAction[] }) {
  return (
    <>
      {actions.map((a) => (
        <DropdownMenuItem key={a.key} disabled={!a.enabled || a.loading} onSelect={a.onSelect}>
          {a.runNote ? <RunNoteIcon tone={a.runNote.tone} /> : ICONS[a.kind]}
          <span className="flex min-w-0 flex-col">
            <span>{a.label}</span>
            {a.reason ? <span className="text-2xs text-muted-foreground">{a.reason}</span> : null}
            {!a.reason && a.runNote ? <span className="text-2xs text-muted-foreground">{a.runNote.text}</span> : null}
          </span>
        </DropdownMenuItem>
      ))}
    </>
  );
}
